import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Popover } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { MachineSettingsDateRangeInput } from 'components/sections/admin-portal/machines/dialogs/settings/common/machine-settings-date-range-input';
import { useMachineMissedDetectionsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineMissedDetectionsDialogState';
import { useMachineShotHistoryDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineShotHistoryDialogState';
import { HistoryTab } from 'enums/machine-settings';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  ShotHistoryTableConfidence,
  ShotHistoryTableMode,
  ShotHistoryTableStaticValues,
} from 'interfaces/i-machine-shots-history-table';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { first } from 'lodash';
import { useState } from 'react';
import { AdminVisionDetectionsService } from 'services/admin/vision-detections';
import { SessionEventsService } from 'services/session-events.service';

const MODE_OPTIONS: IOption[] = Object.values(ShotHistoryTableMode).map(
  (m) => ({
    label: m,
    value: m,
  })
);

const CONFIDENCE_OPTIONS: IOption[] = Object.values(
  ShotHistoryTableConfidence
).map((m) => ({
  label: m,
  value: m,
}));

const TRUE_FALSE_OPTIONS: IOption[] = Object.values(
  ShotHistoryTableStaticValues
).map((m) => ({
  label: m,
  value: m,
}));

interface IProps {
  hitterOptions: IOptionExt[];
  canFilterByHitter: boolean;
}

export const MachineShotHistoryToolbar = (props: IProps) => {
  const { shotFilters, setHistoryTab, setShotFilter, setShotID } =
    useMachineShotHistoryDialogState();

  const { setMissedDetectionID } = useMachineMissedDetectionsDialogState();

  const [fireID, setFireID] = useState('');
  const [loadingFire, setLoadingFire] = useState(false);
  const [showFireInput, setShowFireInput] = useState(false);

  const findShotByFireID = async () => {
    setLoadingFire(true);

    const fire = await SessionEventsService.getFireByID(fireID);

    if (!fire) {
      NotifyHelper.warning({
        message_md: `No fire found for ID ${fireID}`,
      });

      setLoadingFire(false);
      return;
    }

    const result = await SessionEventsService.getFireByID(fireID);
    const shot = first(result?.shot);

    if (shot) {
      setLoadingFire(false);

      setShotID(shot._id);
      setShowFireInput(false);

      setFireID('');
      return;
    }

    // if we can't find a shot, try finding a missed detection and redirecting the user
    const missedDetection =
      await AdminVisionDetectionsService.getInstance().getByFireID(fireID);

    if (missedDetection) {
      NotifyHelper.warning({
        message_md: `Found a missed detection for fire ID ${fireID} but no shot. Redirecting to missed detection details.`,
      });

      setLoadingFire(false);

      setHistoryTab(HistoryTab.MissedDetections);
      setMissedDetectionID(missedDetection._id);
      setShowFireInput(false);

      setFireID('');
      return;
    }

    NotifyHelper.warning({
      message_md: `No shot found for fire ID ${fireID}`,
    });

    setLoadingFire(false);
  };

  return (
    <CommonFormGrid columns={7} gap={RADIX.FLEX.GAP.SM}>
      <MachineSettingsDateRangeInput
        dateRange={shotFilters.dateRange}
        onChange={(dateRange) => setShotFilter({ dateRange })}
        showTime
      />
      <CommonTextInput
        id="shot-history-pitch-name"
        name="name"
        placeholder="Pitch Name"
        value={shotFilters.pitchName}
        onChange={(v) => setShotFilter({ pitchName: v })}
        debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
        rightSlot={
          shotFilters.pitchName.length === 0 ? (
            <MagnifyingGlassIcon />
          ) : (
            <Cross2Icon
              onClick={() => {
                setShotFilter({ pitchName: '' });
              }}
              className="cursor-pointer"
            />
          )
        }
      />
      <CommonSearchInput
        id="shot-history-hitter"
        name="shot-history-hitter"
        options={props.hitterOptions}
        values={shotFilters.hitterID ? [shotFilters.hitterID] : []}
        onChange={(v) => setShotFilter({ hitterID: v[0] ?? '' })}
        placeholder="Hitter Name"
        disabled={!props.canFilterByHitter}
        skipSort
        optional
      />
      <CommonSelectInput
        id="shot-history-mode"
        options={MODE_OPTIONS}
        value={shotFilters.mode}
        onChange={(v) => setShotFilter({ mode: v as ShotHistoryTableMode })}
        placeholder="Mode"
        skipSort
        optional
      />
      <CommonSelectInput
        id="shot-history-confidence"
        options={CONFIDENCE_OPTIONS}
        value={shotFilters.confidence}
        onChange={(v) =>
          setShotFilter({ confidence: v as ShotHistoryTableConfidence })
        }
        placeholder="Confidence"
        skipSort
        optional
      />
      <CommonSelectInput
        id="shot-history-has-video"
        options={TRUE_FALSE_OPTIONS}
        value={shotFilters.hasVideo}
        onChange={(v) =>
          setShotFilter({ hasVideo: v as ShotHistoryTableStaticValues })
        }
        placeholder="Has Video"
        skipSort
        optional
      />
      <Popover.Root
        open={showFireInput}
        onOpenChange={(open) => {
          setShowFireInput(open);

          if (!open) {
            setFireID('');
          }
        }}
      >
        <Popover.Trigger>
          <Button variant={RADIX.BUTTON.VARIANT.SECONDARY}>Fire ID</Button>
        </Popover.Trigger>
        <Popover.Content asChild align="end">
          <Card>
            {/* so the user can submit by hitting Enter */}
            <form>
              <Flex
                p="1"
                align="center"
                gap={RADIX.FLEX.GAP.SM}
                minWidth="300px"
              >
                <Box flexGrow="1">
                  <CommonTextInput
                    id="shot-fire-id"
                    value={fireID}
                    onChange={(v) => setFireID(v ?? '')}
                    disabled={loadingFire}
                    placeholder="Enter your Fire ID"
                    optional
                  />
                </Box>
                <Button
                  variant={RADIX.BUTTON.VARIANT.SECONDARY}
                  color={RADIX.COLOR.SUCCESS}
                  loading={loadingFire}
                  onClick={findShotByFireID}
                >
                  Submit
                </Button>
              </Flex>
            </form>
          </Card>
        </Popover.Content>
      </Popover.Root>
    </CommonFormGrid>
  );
};
